import React, { createContext, useContext, useState, useEffect, useCallback, type ComponentType, type ReactNode } from 'react';

export const THEMES = {
  DARK: 'dark',
  LIGHT: 'light',
} as const;

export type Theme = typeof THEMES[keyof typeof THEMES];

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  cycleTheme: () => void;
  isDark: boolean;
  isLight: boolean;
  themes: typeof THEMES;
}

const THEME_STORAGE_KEY = 'hrave-turnaje-theme';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const isTheme = (value: string | null): value is Theme =>
  value === THEMES.DARK || value === THEMES.LIGHT;

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * ThemeProvider component that wraps the app and provides theme context
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (isTheme(stored)) {
      return stored;
    }
    return THEMES.DARK;
  });

  useEffect(() => {
    document.documentElement.removeAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);

    document.body.className = document.body.className
      .replace(/theme-\w+/g, '')
      .trim();
    document.body.classList.add(`theme-${theme}`);
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    if (isTheme(newTheme)) {
      setThemeState(newTheme);
    } else {
      console.warn(`Invalid theme: ${newTheme}. Using default.`);
      setThemeState(THEMES.DARK);
    }
  }, []);

  const cycleTheme = useCallback(() => {
    setThemeState((current) => {
      switch (current) {
        case THEMES.DARK:
          return THEMES.LIGHT;
        case THEMES.LIGHT:
          return THEMES.DARK;
        default:
          return THEMES.DARK;
      }
    });
  }, []);

  const isDark = theme === THEMES.DARK;
  const isLight = theme === THEMES.LIGHT;

  const value: ThemeContextValue = {
    theme,
    setTheme,
    cycleTheme,
    isDark,
    isLight,
    themes: THEMES,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * HOC to inject theme props into a component
 */
export function withTheme<P extends { theme?: ThemeContextValue }>(Component: ComponentType<P>) {
  return function ThemedComponent(props: Omit<P, 'theme'>) {
    const theme = useTheme();
    return <Component {...(props as P)} theme={theme} />;
  };
}

export default ThemeContext;
